import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Bell, CheckCheck, ArrowRight } from "lucide-react";

const AUTH_BG =
  "https://images.unsplash.com/photo-1486551937199-baf066858de7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODc4NzY1Mjh8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else {
        const ref = new URLSearchParams(window.location.search).get("ref");
        await register(ref ? { ...form, referral_code: ref } : form);
      }
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-[#2A4B46] overflow-hidden">
        <img src={AUTH_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative z-10">
          <div className="flex items-center gap-3 text-[#F9F9F7]">
            <div className="w-10 h-10 bg-[#D36B46] flex items-center justify-center">
              <Bell className="w-5 h-5 text-white" strokeWidth={2.4} />
            </div>
            <span className="font-mono text-sm tracking-[0.25em] uppercase">RemindMe &amp; Do</span>
          </div>
          <p data-testid="app-tagline" className="mt-3 text-xs italic text-[#F9F9F7]/75 text-center max-w-md">
            &ldquo;Your daily life command center. One login. Your day, your business, your money — in under 10 minutes.&rdquo;
          </p>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-serif text-5xl xl:text-6xl leading-none text-[#F9F9F7] tracking-tight">
            Never miss a beat, <span className="text-[#E37B5B] italic">personal</span> or{" "}
            <span className="text-[#E37B5B] italic">professional</span>.
          </h1>
          <p className="mt-6 text-[#A3A39F] leading-relaxed">
            Recurring reminders, bill tracking and a B2B field-service dispatch engine with automated
            wake-up calls, SMS and email — all in one editorial workspace.
          </p>
        </div>
        <div className="relative z-10 flex gap-8 text-[#A3A39F] font-mono text-xs uppercase tracking-widest">
          <span className="flex items-center gap-2"><CheckCheck className="w-4 h-4" /> Advanced Recurrence</span>
          <span className="flex items-center gap-2"><Bell className="w-4 h-4" /> Omni-Channel</span>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#D36B46] flex items-center justify-center">
                <Bell className="w-5 h-5 text-white" strokeWidth={2.4} />
              </div>
              <span className="font-mono text-sm tracking-[0.2em] uppercase">RemindMe &amp; Do</span>
            </div>
            <p data-testid="app-tagline" className="mt-3 text-xs italic text-[var(--text-2)] text-center">
              &ldquo;Your daily life command center. One login. Your day, your business, your money — in under 10 minutes.&rdquo;
            </p>
          </div>

          <p className="label-mono text-[var(--text-2)]">{mode === "login" ? "Welcome back" : "Get started"}</p>
          <h2 className="font-serif text-4xl mt-2 tracking-tight">
            {mode === "login" ? "Sign in" : "Create account"}
          </h2>

          {mode === "register" && (
            <div data-testid="signup-plans" className="mt-5 rounded-xl border border-emerald-500/20 bg-[#0d1730] text-slate-100 p-4">
              <p className="text-[11px] uppercase tracking-widest text-emerald-300 mb-2.5">Start free · upgrade anytime</p>
              <ul className="space-y-1.5 text-xs">
                <li className="flex items-center justify-between gap-2"><span className="text-slate-300">Free — reminders, Cash Miles, Credit Tracker</span><span className="font-mono text-slate-400 shrink-0">$0</span></li>
                <li className="flex items-center justify-between gap-2"><span className="text-slate-300">Premium — MoneyFlex, Budget vs Actual, Tax tips</span><span className="font-mono text-emerald-300 shrink-0">$29.99/mo</span></li>
                <li className="flex items-center justify-between gap-2"><span className="text-slate-300">Business Platinum — teams, Schedule C, business</span><span className="font-mono text-amber-300 shrink-0">$69.99/mo</span></li>
              </ul>
              <a href="/pricing" data-testid="signup-see-plans" className="mt-2.5 inline-block text-[11px] text-emerald-400 hover:underline">See full plans →</a>
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "register" && (
              <>
                <Field label="Full name" testid="reg-name" value={form.name} onChange={set("name")} required />
                <Field label="Phone (for voice/SMS)" testid="reg-phone" value={form.phone} onChange={set("phone")} placeholder="+1 305 555 0100" />
              </>
            )}
            <Field label="Email" type="email" testid="login-email" value={form.email} onChange={set("email")} required />
            <Field label="Password" type="password" testid="login-password" value={form.password} onChange={set("password")} required />

            {err && (
              <div data-testid="auth-error" className="text-sm text-[var(--danger)] border border-[var(--danger)]/30 bg-[var(--danger)]/5 px-3 py-2">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="auth-submit"
              className="group w-full bg-[#1A1A18] text-[#F9F9F7] h-12 flex items-center justify-center gap-2 font-medium hover:bg-[#D36B46] transition-colors disabled:opacity-50"
            >
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </form>

          <div className="mt-6 text-sm text-[var(--text-2)]">
            {mode === "login" ? "New here? " : "Already have an account? "}
            <button
              data-testid="auth-toggle"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }}
              className="text-[#D36B46] font-medium hover:underline"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, type = "text", ...rest }) {
  return (
    <label className="block">
      <span className="label-mono text-[var(--text-2)]">{label}</span>
      <input
        type={type}
        data-testid={testid}
        {...rest}
        className="mt-2 w-full h-12 px-3 border border-[var(--line)] bg-white outline-none focus:border-[#1A1A18] focus:ring-2 focus:ring-[#1A1A18]/10 transition-colors"
      />
    </label>
  );
}
